import React, { useState } from 'react';
import { 
  Plane, Ship, Package, ShieldCheck, Truck, Globe, FileText, 
  CheckCircle2, Users, MapPin, Mail, Building, ArrowRight, Menu, X, Star, BarChart, Clock, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function Home() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <Navbar />
      <Hero />
      <PricingTeaser />
      <About />
      <Services />
      <WhyUs />
      <Process />
      <TargetAudience />
      <Contact />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex-shrink-0 flex items-center -ml-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_aeroship-preview/artifacts/ends4jvh_AeroShipGlobal_Logo.png" 
              alt="AeroShip Global Logo" 
              className="h-24 md:h-32 w-auto object-contain mix-blend-multiply" 
            />
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#about" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">About</a>
            <a href="#services" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Services</a>
            <a href="#process" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Process</a>
            <a href="#contact" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Contact</a>
            <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-6">Get a Quote</Button>
          </div>
          <div className="md:hidden flex items-center">
            <button onClick={() => setIsOpen(!isOpen)} className="text-slate-600 hover:text-slate-900">
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 pt-2 pb-6 space-y-2 shadow-xl">
          <a href="#about" className="block px-3 py-2 text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-md">About</a>
          <a href="#services" className="block px-3 py-2 text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-md">Services</a>
          <a href="#process" className="block px-3 py-2 text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-md">Process</a>
          <a href="#contact" className="block px-3 py-2 text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-md">Contact</a>
          <Button className="w-full mt-4 bg-blue-700 hover:bg-blue-800 text-white rounded-full">Get a Quote</Button>
        </div>
      )}
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative pt-24 pb-32 overflow-hidden bg-slate-900">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXB8ZW58MHx8fHwxNzc4OTU5ODIxfDA&ixlib=rb-4.1.0&q=85" 
          alt="Cargo Ship" 
          className="w-full h-full object-cover opacity-30 mix-blend-overlay"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a192f] via-[#0a192f]/90 to-transparent"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/50 border border-blue-700/50 text-blue-200 text-sm font-medium mb-8 backdrop-blur-sm">
            <span className="flex h-2 w-2 rounded-full bg-blue-400"></span>
            EU Registered Company • Global Network
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight mb-8 leading-[1.1]">
            Global Shipping & <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Trade Solutions.</span><br />
            Simplified.
          </h1>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl leading-relaxed">
            End-to-end logistics, parcel delivery, procurement, and IOR services between India, Europe, and global markets. Your trusted bridge for international trade.
          </p>
          <div className="flex flex-wrap gap-4 items-center">
            <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 py-6 text-lg h-auto shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-colors hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">
              Get a Quote
            </Button>
            <Button variant="outline" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white rounded-full px-8 py-6 text-lg h-auto backdrop-blur-sm">
              Start Shipping
            </Button>
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800 rounded-full px-6 py-6 text-lg h-auto group">
              Request Procurement <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
          
          <div className="mt-16 flex items-center gap-8 border-t border-slate-800 pt-8">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <ShieldCheck className="h-5 w-5 text-blue-400" /> Customs Experts
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Globe className="h-5 w-5 text-blue-400" /> Pan-European Reach
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Clock className="h-5 w-5 text-blue-400" /> Fast Transit
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingTeaser() {
  return (
    <div className="bg-blue-600 py-4 relative z-20 shadow-lg -mt-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between">
          <p className="text-white font-medium text-lg text-center sm:text-left">
            🚀 Special Offer: Cross-border parcel shipping to Europe starting from <span className="font-bold text-yellow-300 text-2xl">€4.99/kg</span>
          </p>
          <Button variant="secondary" className="mt-4 sm:mt-0 rounded-full bg-white text-blue-700 hover:bg-slate-100 font-bold px-6">
            Calculate Price
          </Button>
        </div>
      </div>
    </div>
  );
}

function About() {
  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-blue-100 to-slate-50 rounded-3xl transform rotate-3 -z-10"></div>
            <img 
              src="https://images.unsplash.com/photo-1670121180530-cfcba4438038?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxnbG9iYWwlMjB0cmFkZXxlbnwwfHx8fDE3Nzg5NTk4MjF8MA&ixlib=rb-4.1.0&q=85" 
              alt="Global Trading" 
              className="rounded-2xl shadow-2xl object-cover h-[500px] w-full"
            />
            <div className="absolute -bottom-8 -right-8 bg-white p-6 rounded-2xl shadow-xl max-w-xs border border-slate-100">
              <div className="flex items-center gap-4 mb-2">
                <div className="bg-blue-100 p-3 rounded-xl"><Globe className="h-6 w-6 text-blue-600" /></div>
                <div className="font-bold text-2xl text-slate-900">100+</div>
              </div>
              <p className="text-sm text-slate-600 font-medium">Countries connected through our global logistics network.</p>
            </div>
          </div>
          
          <div>
            <div className="inline-flex items-center gap-2 text-blue-600 font-bold uppercase tracking-wider text-sm mb-4">
              <span className="h-px w-8 bg-blue-600"></span> About Us
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Bridging Continents with Precision
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              <strong>AeroShip Global OÜ</strong> is an Estonia-based global logistics and trading company providing end-to-end solutions including parcel delivery, freight forwarding, procurement, and Importer of Record (IOR) services.
            </p>
            <p className="text-lg text-slate-600 mb-10 leading-relaxed">
              Whether you are an e-commerce seller looking to enter the EU market, or an individual sending packages back home, we eliminate boundaries with our seamless logistics architecture.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="mt-1 bg-blue-50 p-2 rounded-lg text-blue-600">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">Estonia Based</h4>
                  <p className="text-sm text-slate-500">Gateway to European markets</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 bg-blue-50 p-2 rounded-lg text-blue-600">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">India Specialization</h4>
                  <p className="text-sm text-slate-500">Dedicated routes & support</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const services = [
    {
      icon: <Package className="h-6 w-6" />,
      title: "Parcel Shipping",
      desc: "Door-to-door delivery India ↔ Europe. Budget and express options available via ShipMyParcels.",
      color: "bg-blue-50 text-blue-600 border-blue-100"
    },
    {
      icon: <Plane className="h-6 w-6" />,
      title: "Air Freight",
      desc: "Fast, time-critical cargo solutions for urgent business shipments.",
      color: "bg-sky-50 text-sky-600 border-sky-100"
    },
    {
      icon: <Ship className="h-6 w-6" />,
      title: "Sea Freight",
      desc: "Cost-efficient bulk shipping for large cargo and full containers.",
      color: "bg-indigo-50 text-indigo-600 border-indigo-100"
    },
    {
      icon: <ShieldCheck className="h-6 w-6" />,
      title: "Customs Clearance",
      desc: "Full EU import/export handling to ensure compliance and avoid delays.",
      color: "bg-cyan-50 text-cyan-600 border-cyan-100"
    },
    {
      icon: <Truck className="h-6 w-6" />,
      title: "Last-Mile Delivery",
      desc: "Pan-European distribution network ensuring final delivery to the door.",
      color: "bg-teal-50 text-teal-600 border-teal-100"
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Global Trading",
      desc: "Product sourcing from India, UAE, China. Supplier verification & bulk purchasing.",
      color: "bg-violet-50 text-violet-600 border-violet-100"
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "IOR Services",
      desc: "Act as legal importer in EU. Customs compliance & VAT handling for non-EU sellers.",
      color: "bg-slate-100 text-slate-700 border-slate-200"
    }
  ];

  return (
    <section id="services" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center gap-2 text-blue-600 font-bold uppercase tracking-wider text-sm mb-4">
            <span className="h-px w-8 bg-blue-600"></span> Our Expertise <span className="h-px w-8 bg-blue-600"></span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Comprehensive Logistics Services</h2>
          <p className="text-lg text-slate-600">
            From sending a personal parcel to managing complex international supply chains, we have the infrastructure to deliver.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services.map((service, idx) => (
            <Card key={idx} className="border border-slate-200 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-900/5 transition-colors duration-300 group bg-white">
              <CardContent className="p-8">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${service.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  {service.desc}
                </p>
              </CardContent>
            </Card>
          ))}
          
          <Card className="border-none bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-xl flex flex-col justify-center items-center text-center p-8">
            <h3 className="text-2xl font-bold mb-4">Need a Custom Solution?</h3>
            <p className="text-blue-100 mb-6 text-sm">We build tailored logistics pipelines for enterprise clients.</p>
            <Button className="bg-white text-blue-700 hover:bg-slate-100 rounded-full w-full">Contact Sales</Button>
          </Card>
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  const reasons = [
    { title: "Competitive Pricing", desc: "Premium & budget options tailored to your needs.", icon: <BarChart /> },
    { title: "India–Europe Specialization", desc: "Unmatched expertise on these specific trade corridors.", icon: <Globe /> },
    { title: "One-Stop Solution", desc: "Shipping + Sourcing + Import handling under one roof.", icon: <Package /> },
    { title: "Customs Experts", desc: "Navigate complex regulations with zero friction.", icon: <ShieldCheck /> }
  ];

  return (
    <section className="py-24 bg-white border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Why Choose AeroShip?</h2>
            <p className="text-lg text-slate-600 mb-10">We don't just move boxes; we facilitate global business. Our strategic location in Estonia and deep ties with India give us a unique edge.</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {reasons.map((r, i) => (
                <div key={i} className="flex flex-col gap-3">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center border border-blue-100">
                    {r.icon}
                  </div>
                  <h4 className="text-lg font-bold text-slate-900">{r.title}</h4>
                  <p className="text-sm text-slate-600">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1614568111321-aaefe48192e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxjdXN0b21zJTIwcG9ydHxlbnwwfHx8fDE3Nzg5NTk4MjF8MA&ixlib=rb-4.1.0&q=85" 
              alt="Customs and Port" 
              className="rounded-3xl shadow-2xl"
            />
            {/* Playful elements overlaying */}
            <div className="absolute -left-6 top-1/4 bg-white px-6 py-4 rounded-xl shadow-lg border border-slate-100 flex items-center gap-4">
              <div className="bg-green-100 p-2 rounded-full"><CheckCircle2 className="h-5 w-5 text-green-600" /></div>
              <div>
                <div className="font-bold text-slate-900">Customs Cleared</div>
                <div className="text-xs text-slate-500">2 hours ago</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { num: "01", title: "Submit Request", desc: "Get an instant quote for your shipping or procurement needs." },
    { num: "02", title: "Plan & Source", desc: "We optimize routes or verify suppliers for your goods." },
    { num: "03", title: "Customs & Compliance", desc: "All paperwork, VAT, and IOR handled seamlessly." },
    { num: "04", title: "Final Delivery", desc: "Safe, trackable delivery to the final destination." }
  ];

  return (
    <section id="process" className="py-24 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-4xl font-bold mb-6">How It Works</h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">A streamlined process designed to remove the friction from international trade.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connector line for large screens */}
          <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-[2px] bg-gradient-to-r from-blue-600/0 via-blue-600 to-blue-600/0 z-0"></div>
          
          {steps.map((step, i) => (
            <div key={i} className="relative z-10 flex flex-col items-center text-center">
              <div className="w-24 h-24 bg-slate-800 border-4 border-slate-900 rounded-full flex items-center justify-center text-2xl font-bold text-blue-400 mb-6 shadow-[0_0_30px_rgba(37,99,235,0.2)]">
                {step.num}
              </div>
              <h3 className="text-xl font-bold mb-3">{step.title}</h3>
              <p className="text-slate-400">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TargetAudience() {
  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center text-slate-900 mb-16">Who We Serve</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="bg-white border-none shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mb-6">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Diaspora & Students</h3>
              <p className="text-slate-600">Sending packages, documents, or excess baggage back home or to Europe reliably and affordably.</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-none shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4">
              <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded">Popular</span>
            </div>
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-6">
                <Globe className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">E-commerce & Exporters</h3>
              <p className="text-slate-600">Amazon/Shopify sellers and global exporters needing seamless entry into the EU market.</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-none shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mb-6">
                <Building className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">SMEs & Procurement</h3>
              <p className="text-slate-600">Businesses needing reliable product sourcing, supplier verification, and IOR representation.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row">
          
          {/* Form Side */}
          <div className="p-8 md:p-12 lg:w-3/5 bg-white">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Ready to Ship or Source?</h2>
            <p className="text-slate-600 mb-8">Fill out the form below to get an instant quote or speak with an expert.</p>
            
            <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Full Name</label>
                  <Input placeholder="John Doe" className="bg-slate-50 border-slate-200" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Email Address</label>
                  <Input type="email" placeholder="john@company.com" className="bg-slate-50 border-slate-200" />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Service Needed</label>
                <Select>
                  <SelectTrigger className="bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parcel">Parcel Shipping</SelectItem>
                    <SelectItem value="freight">Freight Forwarding</SelectItem>
                    <SelectItem value="procurement">Global Procurement</SelectItem>
                    <SelectItem value="ior">IOR Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Message / Shipment Details</label>
                <Textarea placeholder="Tell us about what you need to move or source..." className="bg-slate-50 border-slate-200 min-h-[120px]" />
              </div>
              
              <Button className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-lg py-6 text-lg">
                Get Instant Quote
              </Button>
            </form>
          </div>
          
          {/* Info Side */}
          <div className="p-8 md:p-12 lg:w-2/5 bg-slate-900 text-white flex flex-col justify-between relative overflow-hidden">
            <div className="absolute bottom-0 right-0 opacity-10 translate-x-1/4 translate-y-1/4">
              <Globe className="w-64 h-64" />
            </div>
            
            <div className="relative z-10">
              <h3 className="text-2xl font-bold mb-8">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Building className="w-6 h-6 text-blue-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-200">AeroShip Global OÜ</h4>
                    <p className="text-slate-400 mt-1">Estonia (EU)</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-blue-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-200">Email Us</h4>
                    <p className="text-slate-400 mt-1">info@aeroshipglobal.com</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Globe className="w-6 h-6 text-blue-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-200">Visit Platform</h4>
                    <p className="text-slate-400 mt-1">shipmyparcels.com</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative z-10 mt-12 pt-8 border-t border-slate-800">
              <p className="text-slate-400 mb-4">Prefer speaking directly?</p>
              <Button variant="outline" className="w-full bg-transparent border-slate-600 text-white hover:bg-slate-800">
                Talk to an Expert
              </Button>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-950 pt-16 pb-8 border-t border-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div className="col-span-1 md:col-span-2">
            <div className="inline-flex items-center mb-6 bg-white px-2 rounded-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_aeroship-preview/artifacts/ends4jvh_AeroShipGlobal_Logo.png" 
                alt="AeroShip Global Logo" 
                className="h-20 w-auto object-contain mix-blend-multiply" 
              />
            </div>
            <p className="text-slate-400 max-w-sm mb-6">
              Global shipping, logistics, procurement, and IOR services simplified for the modern economy.
            </p>
            <div className="flex gap-4">
              {/* Social placeholders */}
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-blue-600 hover:text-white cursor-pointer transition-colors">
                <span className="font-bold">in</span>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-blue-600 hover:text-white cursor-pointer transition-colors">
                <span className="font-bold">f</span>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-blue-600 hover:text-white cursor-pointer transition-colors">
                <span className="font-bold">X</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-bold mb-4">Services</h4>
            <ul className="space-y-2">
              <li><a href="#" className="text-slate-400 hover:text-blue-400 transition-colors">Parcel Shipping</a></li>
              <li><a href="#" className="text-slate-400 hover:text-blue-400 transition-colors">Freight Forwarding</a></li>
              <li><a href="#" className="text-slate-400 hover:text-blue-400 transition-colors">Global Procurement</a></li>
              <li><a href="#" className="text-slate-400 hover:text-blue-400 transition-colors">IOR Services</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-bold mb-4">Company</h4>
            <ul className="space-y-2">
              <li><a href="#" className="text-slate-400 hover:text-blue-400 transition-colors">About Us</a></li>
              <li><a href="#" className="text-slate-400 hover:text-blue-400 transition-colors">Contact</a></li>
              <li><a href="#" className="text-slate-400 hover:text-blue-400 transition-colors">Privacy Policy</a></li>
              <li><a href="#" className="text-slate-400 hover:text-blue-400 transition-colors">Terms & Conditions</a></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} AeroShip Global OÜ. All rights reserved.</p>
          <div className="mt-4 md:mt-0">
            Registered in Estonia (EU)
          </div>
        </div>
      </div>
    </footer>
  );
}

function WhatsAppButton() {
  return (
    <a 
      href="https://wa.me/" 
      target="_blank" 
      rel="noreferrer"
      className="fixed bottom-6 right-6 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform z-50 flex items-center justify-center"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle className="h-8 w-8" />
    </a>
  );
}
